import type { SxProps, Theme } from '@mui/material';

import { SidePanelType } from './types';

const DIVIDER_HALF_HEIGHT = 4; // px, half of the 8px gap between pinned panels

const cardStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  overflow: 'hidden',
});

interface GetPanelStylesArgs {
  type: SidePanelType;
  pinnedPanels: SidePanelType[];
  activePanel: SidePanelType | null;
}

export const getPanelStyles = ({
  type,
  pinnedPanels,
  activePanel,
}: GetPanelStylesArgs): SxProps<Theme> => {
  const isPinned = pinnedPanels.includes(type);
  const isActive = activePanel === type;
  const hasOverlay = activePanel !== null;

  if (isActive) {
    if (pinnedPanels.length > 0) {
      return (theme: Theme) => ({
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        ...cardStyles(theme),
      });
    }
    return (theme: Theme) => ({ height: '100%', ...cardStyles(theme) });
  }

  if (isPinned) {
    if (hasOverlay) {
      return { display: 'none' };
    }

    if (pinnedPanels.length === 1) {
      return (theme: Theme) => ({ height: '100%', ...cardStyles(theme) });
    }

    const panelIndex = pinnedPanels.indexOf(type);

    if (panelIndex === 0) {
      return (theme: Theme) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: `calc(var(--panels-split-ratio) * 100% - ${DIVIDER_HALF_HEIGHT}px)`,
        ...cardStyles(theme),
      });
    }

    return (theme: Theme) => ({
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: `calc((1 - var(--panels-split-ratio)) * 100% - ${DIVIDER_HALF_HEIGHT}px)`,
      ...cardStyles(theme),
    });
  }

  return { display: 'none' };
};
